/**
 * Cron Monitor Component - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/apiConfig';
import { CronJobRun } from '../types/cron';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge } from '../design/primitives';

const CronMonitor: React.FC = () => {
  const [cronRuns, setCronRuns] = useState<CronJobRun[]>([]);
  const [latestRuns, setLatestRuns] = useState<CronJobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobType, setSelectedJobType] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);

  const fetchCronRuns = useCallback(async () => {
    try {
      const params: any = { limit: 50 };
      if (selectedJobType !== 'all') {
        params.job_type = selectedJobType;
      }

      const response = await apiClient.get('/api/cron-runs/', { params });
      setCronRuns(response.data);
    } catch (error) {
      console.error('Failed to fetch cron runs:', error);
    }
  }, [selectedJobType]);

  const fetchLatestRuns = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/cron-runs/latest/');
      setLatestRuns(response.data);
    } catch (error) {
      console.error('Failed to fetch latest runs:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCronRuns(), fetchLatestRuns()]);
      setLoading(false);
    };

    loadData();

    if (autoRefresh) {
      const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [selectedJobType, autoRefresh, fetchCronRuns, fetchLatestRuns]);

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'accent' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'partial':
        return 'warning';
      case 'running':
        return 'accent';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const triggerJob = async (jobType: string, endpoint: string, data?: any) => {
    setTriggering(jobType);
    setTriggerMessage(null);
    try {
      const response = await apiClient.post(`/api/cron-runs/${endpoint}/`, data || {});
      setTriggerMessage(response.data.message);
      // Refresh data after 2 seconds to show the new run
      setTimeout(() => {
        fetchCronRuns();
        fetchLatestRuns();
      }, 2000);
    } catch (error) {
      console.error(`Failed to trigger ${jobType}:`, error);
      setTriggerMessage(`Failed to trigger ${jobType}. Please try again.`);
    } finally {
      setTriggering(null);
      // Clear message after 5 seconds
      setTimeout(() => setTriggerMessage(null), 5000);
    }
  };

  const getJobConfig = (jobType: string) => {
    switch (jobType) {
      case 'email_retrieval':
        return {
          name: 'Email Retrieval',
          endpoint: 'trigger_email_retrieval',
          description: 'Fetch emails from Gmail',
        };
      case 'oura_sync':
        return {
          name: 'Oura Sync',
          endpoint: 'trigger_oura_sync',
          description: 'Sync Oura Ring health data',
          data: { days: 7 },
        };
      case 'maintenance':
        return {
          name: 'Maintenance',
          endpoint: 'trigger_maintenance',
          description: 'Run video URLs, emails, and transcripts',
          data: { transcript_limit: 10 },
        };
      case 'video_urls':
        return {
          name: 'Video URLs',
          endpoint: 'trigger_video_urls',
          description: 'Refresh pre-signed video URLs',
        };
      case 'transcripts':
        return {
          name: 'Transcripts',
          endpoint: 'trigger_transcripts',
          description: 'Generate video transcripts',
          data: { limit: 5 },
        };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '256px',
        }}
      >
        <div
          style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
          }}
        >
          Loading cron job history...
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}
        >
          Cron Job Monitor
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                borderRadius: RADIUS,
                border: `1px solid ${COLORS.border.default}`,
                cursor: 'pointer',
                accentColor: COLORS.accent.default,
              }}
            />
            <span
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.primary,
              }}
            >
              Auto-refresh (30s)
            </span>
          </label>
          <Button
            variant="primary"
            onClick={async () => { await Promise.all([fetchCronRuns(), fetchLatestRuns()]); }}
          >
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Trigger Message */}
      {triggerMessage && (
        <div
          style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: `1px solid ${COLORS.accent.default}`,
            borderRadius: RADIUS,
            padding: SPACING[16],
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.accent.default,
          }}
        >
          {triggerMessage}
        </div>
      )}

      {/* Latest Runs Summary or Trigger Section */}
      <div>
        <h3
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[16],
          }}
        >
          Available Cron Jobs
        </h3>

        {/* Show all job types, with or without run history */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
          {/* Combined Maintenance Job */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING[16] }}>
              <h4
                style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  margin: 0,
                }}
              >
                Combined Maintenance
              </h4>
              <span style={{ marginLeft: SPACING[8] }}>
                <Badge color="accent">Recommended</Badge>
              </span>
            </div>
            {(() => {
              const config = getJobConfig('maintenance');
              const run = latestRuns.find(r => r.job_type === 'maintenance');
              const isRunning = run?.status === 'running';
              if (!config) return null;
              return (
                <Panel
                  style={{
                    border: `2px solid ${COLORS.accent.default}`,
                    boxShadow: `0 0 0 4px rgba(59, 130, 246, 0.1)`,
                    animation: isRunning ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                  }}
                >
                  <div style={{ marginBottom: SPACING[16] }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: SPACING[8],
                      }}
                    >
                      <div>
                        <h5
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '500',
                            color: COLORS.text.primary,
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {config.name}
                          {isRunning && (
                            <span style={{ marginLeft: SPACING[8], display: 'inline-flex', alignItems: 'center' }}>
                              <svg
                                style={{
                                  animation: 'spin 1s linear infinite',
                                  width: '16px',
                                  height: '16px',
                                  color: COLORS.accent.default,
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  style={{ opacity: 0.25 }}
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  style={{ opacity: 0.75 }}
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            </span>
                          )}
                        </h5>
                        <p
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.accent.default,
                            fontWeight: '500',
                            margin: 0,
                            marginTop: SPACING[4],
                            marginBottom: SPACING[8],
                          }}
                        >
                          ⚡ Runs all 3 tasks below in sequence
                        </p>
                      </div>
                      {run && <Badge color={getStatusColor(run.status)}>{run.status_display}</Badge>}
                    </div>
                    <p
                      style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.secondary,
                        margin: 0,
                        marginBottom: SPACING[8],
                      }}
                    >
                      {config.description}
                    </p>
                    {run ? (
                      <div
                        style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.secondary,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: SPACING[4],
                        }}
                      >
                        <p style={{ margin: 0 }}>Last run: {formatDate(run.started_at)}</p>
                        <p style={{ margin: 0 }}>Duration: {formatDuration(run.duration_seconds)}</p>
                        {run.success_count > 0 && (
                          <p style={{ margin: 0, color: COLORS.status.success }}>✓ Success: {run.success_count}</p>
                        )}
                        {run.error_count > 0 && (
                          <p style={{ margin: 0, color: COLORS.status.error }}>✗ Errors: {run.error_count}</p>
                        )}
                        {run.skipped_count > 0 && (
                          <p style={{ margin: 0, color: COLORS.status.warning }}>⊘ Skipped: {run.skipped_count}</p>
                        )}
                      </div>
                    ) : (
                      <p
                        style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          color: COLORS.text.tertiary,
                          margin: 0,
                        }}
                      >
                        No runs yet
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => triggerJob('maintenance', config.endpoint, config.data)}
                    disabled={triggering === 'maintenance'}
                    title={config.description}
                    style={{
                      width: '100%',
                      padding: `${SPACING[8]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: '500',
                      borderRadius: RADIUS,
                      cursor: triggering === 'maintenance' ? 'not-allowed' : 'pointer',
                      opacity: triggering === 'maintenance' ? 0.5 : 1,
                      transition: 'all 0.1s ease',
                      backgroundColor: COLORS.accent.default,
                      color: '#FFFFFF',
                      border: 'none',
                    }}
                    onMouseEnter={(e) => triggering !== 'maintenance' && (e.currentTarget.style.backgroundColor = COLORS.accent.hover)}
                    onMouseLeave={(e) => triggering !== 'maintenance' && (e.currentTarget.style.backgroundColor = COLORS.accent.default)}
                  >
                    {triggering === 'maintenance' ? 'Triggering...' : 'Run All Tasks'}
                  </button>
                </Panel>
              );
            })()}
          </div>

          {/* Individual Jobs */}
          <div>
            <h4
              style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[16],
              }}
            >
              Individual Tasks
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: SPACING[16],
                marginLeft: SPACING[16],
                borderLeft: `2px solid ${COLORS.border.default}`,
                paddingLeft: SPACING[16],
              }}
            >
              {['email_retrieval', 'oura_sync', 'video_urls', 'transcripts'].map((jobType) => {
                const config = getJobConfig(jobType);
                const run = latestRuns.find(r => r.job_type === jobType);
                const isRunning = run?.status === 'running';
                if (!config) return null;
                return (
                  <Panel
                    key={jobType}
                    style={{
                      animation: isRunning ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                    }}
                  >
                    <div style={{ marginBottom: SPACING[16] }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: SPACING[8],
                        }}
                      >
                        <h5
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '500',
                            color: COLORS.text.primary,
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {config.name}
                          {isRunning && (
                            <span style={{ marginLeft: SPACING[8], display: 'inline-flex', alignItems: 'center' }}>
                              <svg
                                style={{
                                  animation: 'spin 1s linear infinite',
                                  width: '16px',
                                  height: '16px',
                                  color: COLORS.accent.default,
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  style={{ opacity: 0.25 }}
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  style={{ opacity: 0.75 }}
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            </span>
                          )}
                        </h5>
                        {run && <Badge color={getStatusColor(run.status)}>{run.status_display}</Badge>}
                      </div>
                      <p
                        style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.secondary,
                          margin: 0,
                          marginBottom: SPACING[8],
                        }}
                      >
                        {config.description}
                      </p>
                      {run ? (
                        <div
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.text.secondary,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: SPACING[4],
                          }}
                        >
                          <p style={{ margin: 0 }}>Last run: {formatDate(run.started_at)}</p>
                          <p style={{ margin: 0 }}>Duration: {formatDuration(run.duration_seconds)}</p>
                          {run.success_count > 0 && (
                            <p style={{ margin: 0, color: COLORS.status.success }}>✓ Success: {run.success_count}</p>
                          )}
                          {run.error_count > 0 && (
                            <p style={{ margin: 0, color: COLORS.status.error }}>✗ Errors: {run.error_count}</p>
                          )}
                          {run.skipped_count > 0 && (
                            <p style={{ margin: 0, color: COLORS.status.warning }}>⊘ Skipped: {run.skipped_count}</p>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            color: COLORS.text.tertiary,
                            margin: 0,
                            marginTop: SPACING[8],
                          }}
                        >
                          No runs yet
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => triggerJob(jobType, config.endpoint, config.data)}
                      disabled={triggering === jobType}
                      title={config.description}
                      style={{
                        width: '100%',
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        borderRadius: RADIUS,
                        cursor: triggering === jobType ? 'not-allowed' : 'pointer',
                        opacity: triggering === jobType ? 0.5 : 1,
                        transition: 'all 0.1s ease',
                        backgroundColor: 'transparent',
                        color: COLORS.text.primary,
                        border: `1px solid ${COLORS.border.default}`,
                      }}
                      onMouseEnter={(e) => triggering !== jobType && (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                      onMouseLeave={(e) => triggering !== jobType && (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {triggering === jobType ? 'Triggering...' : 'Run Task'}
                    </button>
                  </Panel>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
        <label
          style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            fontWeight: '500',
            color: COLORS.text.primary,
          }}
        >
          Filter by job type:
        </label>
        <select
          value={selectedJobType}
          onChange={(e) => setSelectedJobType(e.target.value)}
          style={{
            padding: `${SPACING[8]} ${SPACING[12]}`,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.primary,
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            outline: 'none',
            cursor: 'pointer',
            transition: 'border-color 0.1s ease',
          }}
          onFocus={(e) => (e.target.style.borderColor = COLORS.accent.default)}
          onBlur={(e) => (e.target.style.borderColor = COLORS.border.default)}
        >
          <option value="all">All Jobs</option>
          <option value="email_retrieval">Email Retrieval</option>
          <option value="oura_sync">Oura Sync</option>
          <option value="maintenance">Maintenance</option>
          <option value="video_urls">Video URLs</option>
          <option value="transcripts">Transcripts</option>
        </select>
      </div>

      {/* History Table */}
      <div>
        <h3
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[16],
          }}
        >
          Recent History
        </h3>
        <Panel noPadding>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead
                style={{
                  borderBottom: `1px solid ${COLORS.border.default}`,
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Job Type
                  </th>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Started
                  </th>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Duration
                  </th>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Results
                  </th>
                  <th
                    style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {cronRuns.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: `${SPACING[16]} ${SPACING[16]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: COLORS.text.secondary,
                        textAlign: 'center',
                      }}
                    >
                      No cron runs found
                    </td>
                  </tr>
                ) : (
                  cronRuns.map((run) => {
                    const isRunning = run.status === 'running';
                    return (
                      <tr
                        key={run.id}
                        style={{
                          borderBottom: `1px solid ${COLORS.border.default}`,
                          backgroundColor: isRunning ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                          transition: 'background-color 0.1s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isRunning ? 'rgba(59, 130, 246, 0.05)' : 'transparent')}
                      >
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '500',
                            color: COLORS.text.primary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {run.job_type_display}
                            {isRunning && (
                              <svg
                                style={{
                                  animation: 'spin 1s linear infinite',
                                  marginLeft: SPACING[8],
                                  width: '16px',
                                  height: '16px',
                                  color: COLORS.accent.default,
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  style={{ opacity: 0.25 }}
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  style={{ opacity: 0.75 }}
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Badge color={getStatusColor(run.status)}>{run.status_display}</Badge>
                        </td>
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDate(run.started_at)}
                        </td>
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDuration(run.duration_seconds)}
                        </td>
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
                            {run.success_count > 0 && (
                              <div style={{ color: COLORS.status.success }}>✓ {run.success_count}</div>
                            )}
                            {run.error_count > 0 && (
                              <div style={{ color: COLORS.status.error }}>✗ {run.error_count}</div>
                            )}
                            {run.skipped_count > 0 && (
                              <div style={{ color: COLORS.status.warning }}>⊘ {run.skipped_count}</div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: `${SPACING[16]} ${SPACING[16]}`,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                          }}
                        >
                          {run.error_message && (
                            <details
                              style={{
                                fontSize: TYPOGRAPHY.body.sizes.tiny,
                                marginBottom: SPACING[8],
                              }}
                            >
                              <summary
                                style={{
                                  cursor: 'pointer',
                                  color: COLORS.status.error,
                                  fontWeight: '500',
                                }}
                              >
                                View error message
                              </summary>
                              <div
                                style={{
                                  marginTop: SPACING[8],
                                  padding: SPACING[8],
                                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                  borderRadius: RADIUS,
                                  color: COLORS.status.error,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {run.error_message}
                              </div>
                            </details>
                          )}
                          {Object.keys(run.details).length > 0 && (
                            <details
                              style={{
                                fontSize: TYPOGRAPHY.body.sizes.tiny,
                              }}
                            >
                              <summary
                                style={{
                                  cursor: 'pointer',
                                  color: COLORS.accent.default,
                                }}
                              >
                                View details
                              </summary>
                              <pre
                                style={{
                                  marginTop: SPACING[8],
                                  padding: SPACING[8],
                                  backgroundColor: COLORS.bg.base,
                                  borderRadius: RADIUS,
                                  overflowX: 'auto',
                                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                                  fontFamily: 'monospace',
                                }}
                              >
                                {JSON.stringify(run.details, null, 2)}
                              </pre>
                            </details>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* Add CSS keyframes for animations */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CronMonitor;
